import { useMemo, useEffect } from 'react';

import { sub } from 'date-fns';
import { type DebouncedFunc } from 'lodash';
import debounce from 'lodash.debounce';

import { isValidDate } from '@core';
import { ActivityFilter } from '@store';

export const INITIAL_DATE_RANGE = {
    start: sub(new Date(), { years: 1 }),
    end: new Date(),
};

export const toDateInputValue = (date: Date | null | undefined) => {
    if (!isValidDate(date)) {
        return '';
    }

    return date.toISOString().slice(0, 10);
};

const fromDateInputValue = (value: string) => {
    if (!value) {
        return null;
    }

    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day) {
        return null;
    }

    const parsedDate = new Date(Date.UTC(year, month - 1, day));

    return isValidDate(parsedDate) ? parsedDate : null;
};

export function useDebouncedDateFilter(
    updateFilter: (update: Partial<ActivityFilter>) => void,
    key: 'startDate' | 'endDate',
): DebouncedFunc<(value: string) => void> {
    const debounced = useMemo(
        () =>
            debounce((value: string) => {
                updateFilter({
                    [key]: fromDateInputValue(value),
                });
            }, 500),
        [key, updateFilter],
    );

    useEffect(() => () => debounced.cancel(), [debounced]);

    return debounced;
}
